import React, { useState, useEffect } from 'react';
import { shippingService, ShippingResult } from '../services/shippingService';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Star, 
  ShoppingCart, 
  Truck, 
  Shield, 
  Package, 
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  MapPin,
  ChevronDown
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { translateProductName } from '../utils/translateProductName';
import { analytics } from '../lib/posthog';

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string;
  brand: string;
  category: string;
  description: string;
  rating: number;
  num_reviews: number;
  count_in_stock: number;
  free_shipping: boolean;
}

const ProductDetailNew = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCartMsg, setAddedToCartMsg] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'description' | 'faq' | 'howto'>('description');
  const [cep, setCep] = useState('');
  const [shippingInfo, setShippingInfo] = useState<ShippingResult | null>(null);
  const [calculatingShipping, setCalculatingShipping] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    console.log('🔍 ProductDetailNew - Carregando produto com ID:', id);
    const fetchProduct = async () => {
      if (!id) {
        console.error('❌ ID não fornecido');
        navigate('/produtos');
        return;
      }

      try {
        setLoading(true);
        console.log('�� Buscando produto no Supabase...');
        
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();

        console.log('📦 Resposta:', { data, error });

        if (error) {
          console.error('❌ Erro ao buscar produto:', error);
          navigate('/produtos');
          return;
        }

        if (data) {
          console.log('✅ Produto carregado:', data.name);
          setProduct(data);
          
          // Track product view
          analytics.productViewed({
            id: data.id,
            name: data.name,
            price: data.price,
          });
          
          // Buscar produtos relacionados da mesma categoria
          fetchRelatedProducts(data.category, data.id);
        }
      } catch (error) {
        console.error('❌ Erro:', error);
        navigate('/produtos');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  const fetchRelatedProducts = async (category: string, currentProductId: string) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', category)
        .neq('id', currentProductId)
        .limit(4);

      if (!error && data) {
        setRelatedProducts(data);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos relacionados:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    try {
      setAddingToCart(true);
      await addToCart(product.id, quantity);
      setAddedToCartMsg(true);
      setTimeout(() => setAddedToCartMsg(false), 3000);
    } catch (error) {
      console.error('Erro ao adicionar ao carrinho:', error);
    } finally {
      setAddingToCart(false);
    }
  };

    const handleCalculateFrete = async () => {
    const cleanPostalCode = cep.replace(/\s+/g, '').toUpperCase();
    
    if (cleanPostalCode.length < 4) {
      setShippingError(t('productDetail.validPostalCode'));
      return;
    }

    setCalculatingShipping(true);
    setShippingInfo(null);

    try {
      const result = await shippingService.calculateShippingByPostalCode(cleanPostalCode, product?.price || 0);
      setShippingInfo(result);
    } catch (error) {
      console.error('❌ Erro ao calcular frete:', error);
      setShippingError(t('productDetail.shippingCalcError'));
    } finally {
      setCalculatingShipping(false);
    }
  };


  const handleShare = () => {
    if (navigator.share && product) {
      navigator.share({
        title: product.name,
        text: t('productDetail.shareText', { name: product.name }),
        url: window.location.href,
      }).catch(err => console.log('Erro ao compartilhar:', err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#4ECDC4] mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('productDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">{t('productDetail.notFound')}</p>
          <Link to="/produtos" className="text-[#4ECDC4] hover:text-[#95C11F]">
            {t('productDetail.backToProducts')}
          </Link>
        </div>
      </div>
    );
  }

  const inStock = product.count_in_stock > 0;
  
  // Simular múltiplas imagens (em produção, viria do banco)
  const productImages = [
    product.image_url,
    product.image_url, // Duplicado para demonstração
    product.image_url,
  ];

  // FAQs traduzidas
  const faqs = [
    {
      question: t('productDetail.faqs.question1'),
      answer: t('productDetail.faqs.answer1')
    },
    {
      question: t('productDetail.faqs.question2'),
      answer: t('productDetail.faqs.answer2')
    },
    {
      question: t('productDetail.faqs.question3'),
      answer: t('productDetail.faqs.answer3')
    },
    {
      question: t('productDetail.faqs.question4'),
      answer: t('productDetail.faqs.answer4')
    }
  ];

  // Função para traduzir nomes de categorias
  const getCategoryName = (cat: string) => {
    const categoryMap: Record<string, string> = {
      'detox': t('categories.detoxTitle'),
      'Detox Natural': t('categories.detoxTitle'),
      'emagrecimento': t('categories.weightLossTitle'),
      'weightLoss': t('categories.weightLossTitle'),
      'Queima Gordura': t('categories.weightLossTitle'),
      'Gewichtsverlust': t('categories.weightLossTitle'),
      'fibra': t('categories.fiberTitle'),
      'fiber': t('categories.fiberTitle'),
      'Fibra Digestiva': t('categories.fiberTitle'),
      'Ballaststoffe': t('categories.fiberTitle'),
      'packs': t('categories.packsTitle'),
      'Pacotes': t('categories.packsTitle'),
      'energia': t('categories.energyTitle'),
      'energy': t('categories.energyTitle'),
      'Energia': t('categories.energyTitle')
    };
    
    return categoryMap[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
  };

  // Função para obter descrição traduzida baseada na categoria e nome do produto
  const getTranslatedDescription = (product: Product) => {
    const name = product.name.toLowerCase();
    const category = product.category.toLowerCase();
    const description = product.description.toLowerCase();
    
    // Detectar tipo de produto por palavras-chave (em todos os idiomas)
    if (name.includes('detox') || category.includes('detox') || description.includes('desintox') || description.includes('entgift')) {
      return t('productDetail.descriptions.detox');
    }
    if (name.includes('hibis') || name.includes('colágeno') || name.includes('colageno') || name.includes('collagen') || 
        description.includes('hibis') || description.includes('colágeno') || description.includes('pele') || description.includes('haut')) {
      return t('productDetail.descriptions.collagen');
    }
    if (name.includes('fibra') || name.includes('fiber') || name.includes('mehl') || name.includes('farinha') || 
        description.includes('fibra') || description.includes('trânsito intestinal') || description.includes('darmpassage') || description.includes('ballaststoff')) {
      return t('productDetail.descriptions.fiber');
    }
    if (name.includes('seca barriga') || name.includes('bauch trocken') || name.includes('belly') || name.includes('emagre') || 
        name.includes('queima') || name.includes('goji') || category.includes('emagrecimento') || category.includes('weightloss') ||
        description.includes('emagrecimento') || description.includes('gewichtsabnahme') || description.includes('weight loss')) {
      return t('productDetail.descriptions.weightLoss');
    }
    if (name.includes('energia') || name.includes('energy') || name.includes('guaraná') || name.includes('guarana') ||
        description.includes('energia') || description.includes('energy') || description.includes('desempenho')) {
      return t('productDetail.descriptions.energy');
    }
    
    // Se não houver match, retorna a descrição original
    return product.description;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-[#4ECDC4] transition-colors">
            {t('productDetail.breadcrumb.home')}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/produtos" className="hover:text-[#4ECDC4] transition-colors">
            {t('productDetail.breadcrumb.products')}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link 
            to={`/produtos?category=${product.category}`} 
            className="hover:text-[#4ECDC4] transition-colors"
          >
            {getCategoryName(product.category)}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium truncate max-w-[200px]">
            {translateProductName(product.name)}
          </span>
        </nav>

        {/* Container Principal */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            
            {/* Coluna Esquerda - Galeria de Imagens */}
            <div className="space-y-4">
              {/* Imagem Principal */}
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 relative group">
                <img
                  src={productImages[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {product.free_shipping && (
                    <span className="bg-[#95C11F] text-white text-xs font-bold px-3 py-1 rounded-full">
                      {t('productDetail.shipping.free')}
                    </span>
                  )}
                  {!inStock && (
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {t('productDetail.stock.outOfStock').toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Botões de Ação */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <button
                    onClick={() => setIsFavorite(!isFavorite)}
                    className="bg-white p-2 rounded-full shadow-lg hover:scale-110 transition-transform"
                  >
                    <Heart 
                      className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} 
                    />
                  </button>
                  <button
                    onClick={handleShare}
                    className="bg-white p-2 rounded-full shadow-lg hover:scale-110 transition-transform"
                  >
                    <Share2 className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Miniaturas */}
              <div className="grid grid-cols-4 gap-3">
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === idx 
                        ? 'border-[#4ECDC4] scale-105' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Coluna Direita - Informações do Produto */}
            <div className="flex flex-col">
              
              {/* Marca */}
              {product.brand && (
                <p className="text-[#4ECDC4] font-semibold text-sm uppercase tracking-wide mb-2">
                  {product.brand}
                </p>
              )}

              {/* Nome */}
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {translateProductName(product.name)}
              </h1>

              {/* Rating e Reviews */}
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-gray-600 font-medium">
                  {product.rating.toFixed(1)}
                </span>
                <span className="text-gray-400">|</span>
                <Link to="#reviews" className="text-[#4ECDC4] hover:text-[#95C11F] font-medium">
                  {product.num_reviews} {t('productDetail.reviews.label')}
                </Link>
              </div>

              {/* Preço */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <p className="text-5xl font-bold text-primary-500">
                    CHF {product.price.toFixed(2)}
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  {t('productDetail.price.installments')} CHF {(product.price / 12).toFixed(2)} {t('productDetail.price.interestFree')}
                </p>
              </div>

              {/* Status de Estoque */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                {inStock ? (
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-green-600" />
                    <p className="text-green-600 font-semibold">
                      {t('productDetail.stock.inStock')} - {product.count_in_stock} {t('productDetail.stock.unitsAvailable')}
                    </p>
                  </div>
                ) : (
                  <p className="text-red-600 font-semibold flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    {t('productDetail.stock.outOfStock')}
                  </p>
                )}
              </div>

              {/* Calculadora de Frete */}
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('productDetail.shipping.calculateShipping')}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('productDetail.shipping.postalCode')}
                      value={cep}
                      onChange={(e) => setCep(e.target.value.toUpperCase().slice(0, 10))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                      maxLength={10}
                    />
                  </div>
                  <button
                    onClick={handleCalculateFrete}
                    className="px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    {t('productDetail.shipping.calculate')}
                  </button>
                </div>
                {product.free_shipping && (
                  <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                    <Truck className="w-4 h-4" />
                    {t('productDetail.freeShippingEurope')}
                  </p>
                )}
              </div>

                {calculatingShipping && (
                  <p className="text-gray-600 text-sm mt-3 flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('productDetail.calculatingShipping')}
                  </p>
                )}

                {shippingInfo && !calculatingShipping && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {shippingInfo.isFree ? (
                      <div className="flex items-start gap-2">
                        <Truck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-green-700">{t('productDetail.freeShipping')} 🎉</p>
                          <p className="text-sm text-gray-600 mt-1">{shippingInfo.reason}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {t('productDetail.deliveryTime')}: {shippingInfo.deliveryTime}
                          </p>
                          {shippingInfo.zoneName && (
                            <p className="text-xs text-gray-400 mt-1">
                              {t('productDetail.region')}: {shippingInfo.zoneName}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <Truck className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-gray-900">
                              CHF {shippingInfo.cost.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {shippingInfo.deliveryTime}
                            </p>
                          </div>
                          {shippingInfo.zoneName && (
                            <p className="text-sm text-gray-600 mt-1">{t('productDetail.region')}: {shippingInfo.zoneName}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              
              {/* Quantidade e Adicionar ao Carrinho */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-8">
                <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-5 py-3 text-gray-600 hover:bg-gray-100 font-bold text-xl disabled:opacity-50"
                    disabled={!inStock}
                  >
                    -
                  </button>
                  <span className="px-8 py-3 font-bold text-lg border-x-2 border-gray-300 bg-gray-50">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(product.count_in_stock, quantity + 1))}
                    className="px-5 py-3 text-gray-600 hover:bg-gray-100 font-bold text-xl disabled:opacity-50"
                    disabled={!inStock}
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={!inStock || addingToCart}
                  className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded-lg font-bold text-lg text-white transition-all transform ${
                    inStock
                      ? 'bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] hover:scale-105 hover:shadow-xl'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ShoppingCart className="w-6 h-6" />
                  {addingToCart ? `${t('productDetail.buttons.addToCart')}...` : t('productDetail.buttons.addToCart')}
                </button>
              </div>

              {/* Added to Cart feedback */}
              {addedToCartMsg && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  {t('productDetail.addedToCart')}
                </div>
              )}

              {/* Copied link feedback */}
              {copiedLink && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                  {t('productDetail.linkCopied')}
                </div>
              )}

              {/* Shipping error feedback */}
              {shippingError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {shippingError}
                </div>
              )}

              {/* Benefícios */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Truck className="w-6 h-6 text-[#4ECDC4] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">
                      {product.free_shipping ? t('productDetail.shipping.free') : t('productDetail.benefits.fastDelivery')}
                    </p>
                    <p className="text-xs text-gray-600">{t('productDetail.benefits.allEurope')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Shield className="w-6 h-6 text-[#4ECDC4] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{t('productDetail.benefits.securePurchase')}</p>
                    <p className="text-xs text-gray-600">{t('productDetail.benefits.protectedPayment')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Package className="w-6 h-6 text-[#4ECDC4] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{t('productDetail.benefits.warranty')}</p>
                    <p className="text-xs text-gray-600">30 {t('productDetail.benefits.days')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs de Conteúdo */}
        <div className="mt-8 bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('description')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'description'
                  ? 'text-[#4ECDC4] border-b-2 border-[#4ECDC4]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('productDetail.tabs.description')}
            </button>
            <button
              onClick={() => setActiveTab('faq')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'faq'
                  ? 'text-[#4ECDC4] border-b-2 border-[#4ECDC4]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('productDetail.tabs.faq')}
            </button>
            <button
              onClick={() => setActiveTab('howto')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'howto'
                  ? 'text-[#4ECDC4] border-b-2 border-[#4ECDC4]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('productDetail.tabs.howToUse')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('productDetail.about.title')} {translateProductName(product.name)}
                </h3>
                <p className="text-gray-700 leading-relaxed text-lg mb-6">
                  {getTranslatedDescription(product)}
                </p>
                <div className="bg-gradient-to-r from-[#4ECDC4]/10 to-[#95C11F]/10 p-6 rounded-xl">
                  <h4 className="font-bold text-lg text-gray-900 mb-3">
                    ✨ {t('productDetail.about.mainBenefits')}:
                  </h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#4ECDC4] font-bold">✓</span>
                      <span>{t('productDetail.benefits.benefit1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#4ECDC4] font-bold">✓</span>
                      <span>{t('productDetail.benefits.benefit2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#4ECDC4] font-bold">✓</span>
                      <span>{t('productDetail.benefits.benefit3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#4ECDC4] font-bold">✓</span>
                      <span>{t('productDetail.benefits.benefit4')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'faq' && (
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('productDetail.tabs.faqTitle')}
                </h3>
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                      <ChevronDown className="w-5 h-5 text-gray-600 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            )}

            {activeTab === 'howto' && (
              <div className="prose max-w-none">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('productDetail.tabs.howToUseTitle')}
                </h3>
                <div className="bg-gradient-to-r from-[#4ECDC4]/10 to-[#95C11F]/10 p-6 rounded-xl mb-6">
                  <h4 className="font-bold text-lg text-gray-900 mb-4">
                    📋 {t('productDetail.howToUse.preparationMethod')}:
                  </h4>
                  <ol className="space-y-3 text-gray-700">
                    <li className="flex gap-3">
                      <span className="font-bold text-[#4ECDC4] flex-shrink-0">1.</span>
                      <span>{t('productDetail.howToUse.step1')}</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-[#4ECDC4] flex-shrink-0">2.</span>
                      <span>{t('productDetail.howToUse.step2')}</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-[#4ECDC4] flex-shrink-0">3.</span>
                      <span>{t('productDetail.howToUse.step3')}</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-[#4ECDC4] flex-shrink-0">4.</span>
                      <span>{t('productDetail.howToUse.step4')}</span>
                    </li>
                  </ol>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ {t('productDetail.howToUse.important')}:</strong> {t('productDetail.howToUse.disclaimer')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Produtos Relacionados */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">{t('productDetail.relatedProducts.title')}</h2>
              <Link
                to={`/produtos?category=${product.category}`}
                className="text-[#4ECDC4] hover:text-[#95C11F] font-semibold flex items-center gap-1"
              >
                {t('productDetail.relatedProducts.viewAll')}
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/producto/${relatedProduct.id}`)}
                >
                  <div className="aspect-square overflow-hidden bg-gray-100">
                    <img
                      src={relatedProduct.image_url}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-[#4ECDC4] text-xs font-semibold uppercase mb-1">
                      {relatedProduct.brand}
                    </p>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 h-12">
                      {translateProductName(relatedProduct.name)}
                    </h3>
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(relatedProduct.rating)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="text-xs text-gray-600 ml-1">
                        ({relatedProduct.num_reviews})
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-primary-500">
                      CHF {relatedProduct.price.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botão Voltar */}
        <div className="mt-12">
          <Link
            to="/produtos"
            className="inline-flex items-center text-[#4ECDC4] hover:text-[#95C11F] font-semibold transition-colors"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {t('productDetail.buttons.backToProducts')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailNew;
